import { isJsonObjectValue, type JsonObject } from "@/types/json"

import { isRecord, readString } from "./actionArgumentReaders"

interface ArgRow {
  key: string
  value: string
}

function countLabel(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? "" : "s"}`
}

// A group's members are every simulation matching any of its selectors.
const GROUP_SELECTORS = [
  ["simulation_ids", "simulation"],
  ["run_ids", "run"],
  ["persona_ids", "persona"],
  ["test_case_ids", "test case"],
] as const

function describeGroup(group: Record<string, unknown>): string {
  const name = readString(group, "name") ?? "?"
  const members = GROUP_SELECTORS.flatMap(([key, noun]) => {
    // Count entries rather than strictly parsing them: the card must show the
    // size of what will execute, which is the raw signed list.
    const value = group[key]
    const count = Array.isArray(value) ? value.length : 0
    return count > 0 ? [countLabel(count, noun)] : []
  })
  return members.length > 0 ? `${name} (${members.join(" + ")})` : name
}

/** One card row per custom report dimension: its groups and their sizes,
 *  never the raw simulation ids a group can carry by the hundred. Rows are
 *  numbered when there are several, since the card keys rows by label. */
export function summarizeCustomDimensions(value: unknown): ArgRow[] {
  if (!Array.isArray(value)) {
    return []
  }
  const dimensions = value.filter(isRecord)
  return dimensions.map((dimension, index) => {
    const groups = Array.isArray(dimension.groups)
      ? dimension.groups.filter(isRecord).map(describeGroup)
      : []
    const hidden =
      dimension.hide_unassigned === true ? "; unassigned hidden" : ""
    const name = readString(dimension, "name") ?? "?"
    return {
      key: dimensions.length > 1 ? `dimension ${index + 1}` : "dimension",
      value: `${name}: ${groups.join(", ")}${hidden}`,
    }
  })
}

/** The create_report proposal's custom dimensions, kept as the signed JSON. */
export function readCustomDimensions(
  args: Record<string, unknown>,
): JsonObject[] | undefined {
  const value = args.custom_dimensions
  return Array.isArray(value) ? value.filter(isJsonObjectValue) : undefined
}
